/**
 * DNSInstructions Component
 *
 * Displays both A record and CNAME record instructions with copy-to-clipboard functionality.
 * Used during domain setup to guide users on adding DNS records.
 *
 * User must add BOTH records:
 * 1. A record: points domain to Load Balancer IP (for routing)
 * 2. CNAME record: for SSL certificate verification (ACME challenge)
 *
 * Pattern reference: src/modules/webplacements/components/WebPlacementDialog.tsx
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Paper,
  Alert,
  Divider,
  useTheme,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import type { CnameRecord, DnsRecords } from '../types/customDomain.types';
import { CUSTOM_DOMAIN_TEST_IDS, type DnsRecordTestKey } from '@modules/custom-domains/testIds';

/**
 * Copy feedback duration in milliseconds
 */
const COPY_FEEDBACK_DURATION = 2000;

/**
 * Component props
 */
export interface DNSInstructionsProps {
  /** New format: both DNS records */
  dnsRecords?: DnsRecords;
  /** Legacy: CNAME record only (for backward compatibility) */
  cnameRecord?: CnameRecord;
  /** Simplified host value for display (e.g., "_acme-challenge.promo") */
  displayHost?: string;
  /** Show info alert about DNS propagation */
  showPropagationNote?: boolean;
  /** Compact layout for dialogs */
  compact?: boolean;
}

/**
 * Copyable Field subcomponent
 */
interface CopyableFieldProps {
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
  testIdKey: DnsRecordTestKey;
}

const CopyableField: React.FC<CopyableFieldProps> = ({
  label,
  value,
  copied,
  onCopy,
  testIdKey,
}) => {
  const theme = useTheme();

  return (
    <TextField
      label={label}
      value={value}
      fullWidth
      size="small"
      margin="dense"
      data-testid={CUSTOM_DOMAIN_TEST_IDS.dnsRow(testIdKey)}
      slotProps={{
        input: {
          readOnly: true,
          sx: {
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            backgroundColor: theme.palette.action.hover,
          },
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                <IconButton
                  onClick={onCopy}
                  size="small"
                  color={copied ? 'success' : 'default'}
                  aria-label={copied ? 'Copied' : 'Copy to clipboard'}
                  data-testid={CUSTOM_DOMAIN_TEST_IDS.dnsCopyBtn(testIdKey)}
                >
                  {copied ? <CheckIcon /> : <ContentCopyIcon />}
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};

/**
 * Copied field identifier
 */
type CopiedFieldId = 'a-host' | 'a-value' | 'cname-host' | 'cname-target' | 'name' | 'data' | null;

/**
 * DNS Instructions Component
 *
 * Displays both A record and CNAME record with copy buttons for easy DNS configuration.
 * Supports both new format (dnsRecords) and legacy format (cnameRecord).
 */
export const DNSInstructions: React.FC<DNSInstructionsProps> = ({
  dnsRecords,
  cnameRecord,
  displayHost,
  showPropagationNote = true,
  compact = false,
}) => {
  const theme = useTheme();
  const [copiedField, setCopiedField] = useState<CopiedFieldId>(null);

  /**
   * Handle copy to clipboard
   */
  const handleCopy = useCallback(async (value: string, field: CopiedFieldId) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), COPY_FEEDBACK_DURATION);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  }, []);

  // New format: both records
  if (dnsRecords) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: compact ? 1.5 : 2,
          bgcolor: theme.palette.action.hover,
          borderRadius: theme.shape.borderRadius,
        }}
      >
        <Typography
          variant={compact ? 'body2' : 'subtitle2'}
          fontWeight={600}
          gutterBottom
        >
          Add these DNS records to your DNS provider:
        </Typography>

        {/* Record 1: A Record */}
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            color="primary.main"
            sx={{ mb: 1 }}
          >
            1. A Record (for routing)
          </Typography>
          <CopyableField
            label="Host / Name"
            value={dnsRecords.aRecord.host}
            copied={copiedField === 'a-host'}
            onCopy={() => handleCopy(dnsRecords.aRecord.host, 'a-host')}
            testIdKey="a-host"
          />
          <CopyableField
            label="Points to / Value"
            value={dnsRecords.aRecord.value}
            copied={copiedField === 'a-value'}
            onCopy={() => handleCopy(dnsRecords.aRecord.value, 'a-value')}
            testIdKey="a-value"
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Record 2: CNAME Record */}
        <Box>
          <Typography
            variant="body2"
            fontWeight={600}
            color="primary.main"
            sx={{ mb: 1 }}
          >
            2. CNAME Record (for SSL verification)
          </Typography>
          <CopyableField
            label="Host / Name"
            value={dnsRecords.cnameRecord.host}
            copied={copiedField === 'cname-host'}
            onCopy={() => handleCopy(dnsRecords.cnameRecord.host, 'cname-host')}
            testIdKey="cname-host"
          />
          <CopyableField
            label="Points to / Target"
            value={dnsRecords.cnameRecord.target}
            copied={copiedField === 'cname-target'}
            onCopy={() => handleCopy(dnsRecords.cnameRecord.target, 'cname-target')}
            testIdKey="cname-value"
          />
        </Box>

        {showPropagationNote && (
          <Alert
            severity="info"
            sx={{
              mt: 2,
              '& .MuiAlert-message': {
                fontSize: compact ? '0.8125rem' : '0.875rem',
              },
            }}
          >
            <strong>Both records are required.</strong> DNS changes can take up to 48 hours to propagate.
            We'll automatically verify and proceed with SSL certificate setup.
          </Alert>
        )}
      </Paper>
    );
  }

  // Legacy format: CNAME only (backward compatibility)
  if (!cnameRecord) {
    return null;
  }

  // Use displayHost if provided, otherwise use full cnameRecord.name
  const hostValue = displayHost || cnameRecord.name;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: compact ? 1.5 : 2,
        bgcolor: theme.palette.action.hover,
        borderRadius: theme.shape.borderRadius,
      }}
    >
      <Typography
        variant={compact ? 'body2' : 'subtitle2'}
        fontWeight={600}
        gutterBottom
      >
        Add this CNAME record to your DNS provider:
      </Typography>

      <Box sx={{ mt: 1 }}>
        {/* Host / Name field */}
        <CopyableField
          label="Host / Name"
          value={hostValue}
          copied={copiedField === 'name'}
          onCopy={() => handleCopy(hostValue, 'name')}
          testIdKey="cname-host"
        />

        {/* Target / Points To field */}
        <CopyableField
          label="Points to / Target"
          value={cnameRecord.data}
          copied={copiedField === 'data'}
          onCopy={() => handleCopy(cnameRecord.data, 'data')}
          testIdKey="cname-value"
        />
      </Box>

      {showPropagationNote && (
        <Alert
          severity="info"
          sx={{
            mt: 2,
            '& .MuiAlert-message': {
              fontSize: compact ? '0.8125rem' : '0.875rem',
            },
          }}
        >
          DNS changes can take up to 48 hours to propagate.
          We'll automatically verify and proceed with SSL certificate setup.
        </Alert>
      )}
    </Paper>
  );
};

/**
 * Extract simplified host from full CNAME name
 * e.g., "_acme-challenge.promo.client.com" → "_acme-challenge.promo"
 */
export function extractDisplayHost(fullName: string, domain: string): string {
  // Remove the domain suffix to get just the host part
  const domainSuffix = `.${domain}`;
  if (fullName.endsWith(domainSuffix)) {
    return fullName.slice(0, -domainSuffix.length);
  }
  // Fallback: return the part before the TLD
  const parts = fullName.split('.');
  if (parts.length > 2) {
    return parts.slice(0, -2).join('.');
  }
  return fullName;
}

export default DNSInstructions;
